/* CUSTOM_FREIGHTHUB_BY_CLAUDE (Ryan 2026-10-14, Freight Hub H1) - the Freight Hub page.
   Planner: Freight-Hub/FREIGHT-HUB-PLANNER.md (root docs repo).

   One call: GET /admin/freight-hub/summary (rfq-worker, admin only) answers five sections, each
   {ok, data} or {ok:false, error}. Each section is one tile; a section that failed shows its own
   error and every other tile still draws. READ ONLY: the only other call is the per-supplier
   "check live" button on the missing-weights tile (GET /freight-weight-gaps?vendor=, which reads
   Shopify live and writes nothing). */
(function () {
  'use strict';

  var SECTION_ORDER = ['priceTable', 'parity', 'accuracy', 'gaps', 'rocket', 'compare'];
  var TITLES = {
    priceTable: 'Stored freight prices',
    parity: 'Website vs API (Rocket)',
    accuracy: 'Weight guesses vs what shipped',
    gaps: 'Missing weights and dims',
    rocket: 'Rocket API',
    compare: 'Stored price vs real rates', // CUSTOM_FREIGHTCOMPARE_BY_CLAUDE (H4)
  };
  var SOURCES = {
    priceTable: 'The overnight price table (its settings are the Stored Freight Prices panel below).',
    parity: 'Every website rate re-checked through the API, flagged when a carrier is more than $5 apart.',
    accuracy: 'Our calculator weight vs the measured shipment, per product.',
    gaps: 'The Product Rules list, rebuilt every night. Active products only.',
    rocket: 'The last 200 Rocket calls and the 15-a-minute limiter.',
    compare: 'Every real Rocket price and every billed load, next to what the stored table would have said for the same load. Last 90 days.',
  };
  /* the worker's own extra names (lib/freight-rate-cache.js EXTRA_WORDS); a set is joined with '+' */
  var EXTRA_WORDS = {
    residential: 'Residential delivery', liftGate: 'Lift gate', insideDelivery: 'Inside delivery',
    limitedAccess: 'Limited access', deliveryAppointment: 'Delivery appointment', notify: 'Notify before delivery',
  };

  // ── pure helpers (exposed for tests) ────────────────────────────────────────────────────────

  function n0(v) { return v == null || !isFinite(v) ? '0' : Math.round(v).toLocaleString('en-US'); }
  function money(v) { return v == null || !isFinite(v) ? '-' : '$' + Number(v).toFixed(2); }
  function secs(ms) { return ms == null || !isFinite(ms) ? '-' : (ms / 1000).toFixed(1) + ' s'; }
  function pct(v) { return v == null || !isFinite(v) ? '-' : (Math.round(v * 10) / 10) + '%'; }

  function ageText(iso, now) {
    var t = Date.parse(iso || '');
    if (!isFinite(t)) return 'never';
    var h = ((now || Date.now()) - t) / 3600000;
    if (h < 1) return Math.max(1, Math.round(h * 60)) + ' min ago';
    if (h < 48) return Math.round(h) + 'h ago';
    return Math.round(h / 24) + ' days ago';
  }

  /** "liftGate+residential" -> "Lift gate + Residential delivery". Pure. */
  function extraLabel(set) {
    return String(set || '').split('+').map(function (w) { return EXTRA_WORDS[w] || w; }).join(' + ');
  }

  /** One fee-schedule row in words. Pure. */
  function feeWords(row) {
    if (!row || !row.learned) return 'not measured yet';
    if (row.model === 'per-cwt') return money(row.amount) + ' per 100 lb' + (row.minimum != null ? ' (min ' + money(row.minimum) + ')' : '');
    return money(row.amount) + ' flat';
  }

  /** "off 18% heavy" / "off 11% light" / "about even". Pure. */
  function missWords(p) {
    if (!p || p.medianMissPct == null) return '-';
    var dir = p.bias === 'high' ? ' heavy' : p.bias === 'low' ? ' light' : '';
    return 'off ' + pct(p.medianMissPct) + dir;
  }

  /** The suppliers to offer a live Shopify check for, most gaps first. Pure. */
  function liveCheckVendors(gapsData, max) {
    var k = (gapsData && gapsData.kinds || []).filter(function (x) { return x.id === 'weight-missing'; })[0];
    var by = (k && k.byVendor) || {};
    return Object.keys(by).filter(function (v) { return v && v !== '(none)'; })
      .sort(function (a, b) { return by[b] - by[a] || a.localeCompare(b); })
      .slice(0, max || 6)
      .map(function (v) { return { vendor: v, count: by[v] }; });
  }

  /* CUSTOM_FREIGHTCOMPARE_BY_CLAUDE (Freight Hub H4) - the stored-price-vs-real words. */
  var STORED_WORDS = {
    hit: 'stored price', 'stale-hit': 'stored price, out of date', 'class-estimate': 'another class, scaled',
    fallback: 'nearest stored price', unavailable: 'no stored price', error: 'could not look up',
  };

  /** "+6.2% over" / "3.1% under" / "even". Pure. */
  function gapWords(p) {
    if (p == null || !isFinite(p)) return '-';
    if (p === 0) return 'even';
    return p > 0 ? '+' + pct(p) + ' over' : pct(-p) + ' under';
  }

  /** Why a stored answer came out where it did: the source, the band it was priced at, its age. Pure. */
  function storedWhy(r) {
    var st = (r && r.stored) || {};
    var bits = [STORED_WORDS[st.source] || st.source || '-'];
    if (st.wb) bits.push('priced at ' + n0(st.wb) + ' lb, class ' + (st.cb || '-'));
    if (st.ageDays != null) bits.push(st.ageDays + ' days old');
    if (r && r.mixedClass) bits.push('mixed classes');
    if (st.complete === false) bits.push('an extra not measured yet');
    return bits.join(' \u00b7 ');
  }

  /** Is the whole summary shaped like v1? Pure. */
  function summaryOk(d) {
    return !!(d && d.v === 1 && d.sections && SECTION_ORDER.every(function (n) { return d.sections[n] && typeof d.sections[n].ok === 'boolean'; }));
  }

  // ── DOM ─────────────────────────────────────────────────────────────────────────────────────

  function el(tag, attrs, kids) {
    var node = document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (!Object.prototype.hasOwnProperty.call(attrs, k) || attrs[k] == null) continue;
      if (k === 'text') node.textContent = attrs[k];
      else if (k === 'class') node.className = attrs[k];
      else if (k.indexOf('on') === 0) node.addEventListener(k.slice(2), attrs[k]);
      else node.setAttribute(k, attrs[k]);
    }
    (kids || []).forEach(function (c) { if (c) node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return node;
  }
  function clear(node) { while (node && node.firstChild) node.removeChild(node.firstChild); }
  function api() { return window.RepPortal && window.RepPortal.API; }

  var root = null;
  var live = {}; // vendor -> {loading, data, error}

  function notice(kind, title, lead, extra) {
    return el('div', { class: 'cfh-notice cfh-notice--' + kind, role: kind === 'error' ? 'alert' : null }, [
      el('p', { class: 'cfh-notice__title', text: title }),
      lead ? el('p', { class: 'cfh-notice__lead', text: lead }) : null,
      extra || null,
    ]);
  }

  function stat(label, value, cls) {
    return el('div', { class: 'cfh-stat' + (cls ? ' ' + cls : '') }, [
      el('span', { class: 'cfh-stat__value', text: value }),
      el('span', { class: 'cfh-stat__label', text: label }),
    ]);
  }
  function stats(list) { return el('div', { class: 'cfh-stats' }, list); }
  function line(text, cls) { return el('p', { class: 'cfh-line' + (cls ? ' ' + cls : ''), text: text }); }

  function tile(name, section, body) {
    var bad = !section || !section.ok;
    return el('section', { class: 'cfh-tile' + (bad ? ' cfh-tile--bad' : ''), 'data-section': name }, [
      el('h2', { class: 'cfh-tile__title', text: TITLES[name] }),
      el('p', { class: 'cfh-tile__source', text: SOURCES[name] }),
      bad
        ? el('div', { class: 'cfh-tile__error', role: 'alert' }, [
          el('p', { class: 'cfh-line cfh-bad', text: 'This section could not load: ' + ((section && section.error) || 'no answer') }),
          el('p', { class: 'cfh-line cfh-muted', text: 'The other sections are not affected. This is a loading problem, not an empty result.' }),
        ])
        : body,
    ]);
  }

  // ── the five tiles ──────────────────────────────────────────────────────────────────────────

  function priceTableBody(d) {
    var t = d.table || {}; var lk = d.lookups || {}; var cfg = d.config || {};
    var fees = (d.feeSchedule && d.feeSchedule.schedule) || {};
    var feeRows = Object.keys(fees).map(function (set) {
      return el('tr', null, [el('td', { text: extraLabel(set) }), el('td', { text: feeWords(fees[set]) })]);
    });
    return el('div', null, [
      stats([
        stat('cells priced', n0(t.cells)),
        stat('fresh', n0(t.fresh)),
        stat('stale', n0(t.stale), t.stale ? 'cfh-stat--warn' : ''),
        stat('failed', n0(t.failed), t.failed ? 'cfh-stat--bad' : ''),
        stat('lanes', n0(d.lanes && d.lanes.count)),
      ]),
      stats([
        stat('hit rate', lk.lookups ? pct(lk.hitRatePct) : '-'),
        stat('lookups', n0(lk.lookups)),
        stat('live median', secs(lk.liveMedianMs)),
        stat('live p90', secs(lk.liveP90Ms)),
      ]),
      line('Overnight filler ' + (cfg.enabled ? 'ON' : 'OFF') + ' · extras test ' + (cfg.feeTest ? 'ON' : 'OFF') + ' · Rocket API ' + (d.rocketApiConfigured ? 'set' : 'not set'), 'cfh-muted'),
      feeRows.length
        ? el('table', { class: 'cfh-table' }, [el('thead', null, [el('tr', null, [el('th', { text: 'Extra' }), el('th', { text: 'What it adds' })])]), el('tbody', null, feeRows)])
        : line('No extras measured yet (the extras test has not finished).', 'cfh-muted'),
      d.seed ? seedBody(d.seed, cfg) : null,
    ]);
  }

  /* CUSTOM_FREIGHTSEED_BY_CLAUDE (Freight Hub H8): every lane - coverage by state (worst first), the pace,
     how much prices moved between runs, the learned class ratios, and the switch + dock ticks (a save
     through the price table's own settings route). */
  var seedState = { showAll: false, edit: null, saving: false, msg: '' };
  var STATE_ROWS = 8;

  function seedBody(sd, cfg) {
    if (sd.error) return el('div', { class: 'cfh-seed' }, [el('h3', { class: 'cfh-seed__title', text: 'Every lane' }), line('Could not read the seed list: ' + sd.error, 'cfh-bad')]);
    var cov = sd.coverage || {}; var pace = sd.pace || {};
    if (!seedState.edit) seedState.edit = { seed: !!sd.switchOn, docks: (cfg.seedDocks || []).slice() };
    var states = cov.byState || [];
    var shown = seedState.showAll ? states : states.slice(0, STATE_ROWS);
    var stateRows = shown.map(function (s) {
      return el('tr', null, [el('td', { text: s.state }), el('td', { text: n0(s.stored) }), el('td', { text: n0(s.live) }), el('td', { text: pct(s.pct), class: s.pct >= 100 ? 'cfh-good' : (s.pct < 50 ? 'cfh-warn' : null) })]);
    });
    var ch = (sd.changes && sd.changes.overall) || {};
    var ratios = (sd.classRatios || []).filter(function (r) { return r.n >= 5; });
    var dockLabel = {}; (sd.docks || []).forEach(function (d) { dockLabel[d.origin] = d.label; });
    return el('div', { class: 'cfh-seed' }, [
      el('h3', { class: 'cfh-seed__title', text: 'Every lane' }),
      line(sd.on ? 'Seeding ON: every chosen dock to every ZIP area in the lower 48 and DC.' : (sd.switchOn ? 'Seeding is switched on but the overnight filler is OFF, so nothing is priced.' : 'Seeding OFF: only lanes from deals are stored.'), sd.on ? null : 'cfh-muted'),
      stats([
        stat('lanes covered', n0(cov.stored) + ' of ' + n0(cov.cells)),
        stat('covered', pct(cov.pct), cov.pct >= 100 ? '' : 'cfh-stat--warn'),
        stat(cov.missing ? 'more nights to fill' : 'filled', cov.missing ? '~' + n0(pace.nightsToFill) : 'yes'),
        stat('failed', n0(cov.failed), cov.failed ? 'cfh-stat--bad' : ''),
      ]),
      line('Docks: ' + (sd.docks || []).map(function (d) { return d.label + ' (class ' + d.classBand + ')'; }).join(' · '), 'cfh-muted'),
      states.length ? el('table', { class: 'cfh-table' }, [
        el('thead', null, [el('tr', null, [el('th', { text: 'State' }), el('th', { text: 'Stored' }), el('th', { text: 'Live' }), el('th', { text: 'Covered' })])]),
        el('tbody', null, stateRows),
      ]) : null,
      states.length > STATE_ROWS ? el('button', { class: 'cfh-btn cfh-btn--ghost', type: 'button', text: seedState.showAll ? 'Show fewer states' : 'Show all ' + states.length + ' states', onclick: function () { seedState.showAll = !seedState.showAll; if (lastData) render(lastData); } }) : null,
      line('Pace: up to ' + n0(pace.callsPerNightMax) + ' Rocket prices a night (10 PM - 6 AM). Re-prices: lanes in use every ' + n0(pace.refreshInUseDays) + ' days, the rest every ' + n0(pace.refreshUnusedDays) + ' (at most ' + n0(pace.refreshCapPerNight) + ' a night). Tonight: ' + n0((pace.tonight || {}).missing) + ' new, ' + n0((pace.tonight || {}).refresh) + ' re-priced, ' + n0((pace.tonight || {}).drift) + ' drift, ' + n0((pace.tonight || {}).classSample) + ' class samples.', 'cfh-muted'),
      line(ch.n ? 'Price change between runs: ' + n0(ch.n) + ' re-prices, ' + pct(ch.unchangedPct) + ' unchanged, typical change ' + pct(ch.medianAbsPct) + ', 9 in 10 within ' + pct(ch.p90AbsPct) + '.' : 'Price change between runs: none yet.'),
      line((sd.changes && sd.changes.suggestion) || '', 'cfh-muted'),
      ratios.length
        ? el('table', { class: 'cfh-table' }, [
          el('thead', null, [el('tr', null, [el('th', { text: 'Dock' }), el('th', { text: 'Class' }), el('th', { text: 'x stored price' }), el('th', { text: 'Prices' })])]),
          el('tbody', null, ratios.map(function (r) { return el('tr', null, [el('td', { text: dockLabel[r.origin] || r.origin }), el('td', { text: r.base + ' -> ' + r.band }), el('td', { text: ratioText(r) }), el('td', { text: n0(r.n) })]); })),
        ])
        : line('Class ratios: still learning (a class we did not store is estimated once 5 real prices are in).', 'cfh-muted'),
      seedControls(sd),
    ]);
  }

  function ratioText(r) { return r.p75 == null ? '-' : 'x' + (Math.round(r.p75 * 100) / 100) + ' (median x' + (Math.round(r.median * 100) / 100) + ')'; }

  function seedControls(sd) {
    var e = seedState.edit;
    var boxes = (sd.dockChoices || []).map(function (c) {
      var on = e.docks.indexOf(c.key) >= 0;
      return el('label', { class: 'cfh-check' }, [
        el('input', { type: 'checkbox', checked: on ? 'checked' : null, 'data-dock': c.key, onchange: function () {
          var i = e.docks.indexOf(c.key); if (i >= 0) e.docks.splice(i, 1); else e.docks.push(c.key);
        } }),
        c.label + ' (class ' + c.classBand + ')',
      ]);
    });
    return el('div', { class: 'cfh-seed__controls' }, [
      el('label', { class: 'cfh-check' }, [
        el('input', { type: 'checkbox', checked: e.seed ? 'checked' : null, 'data-seed-switch': '1', onchange: function () { e.seed = !e.seed; } }),
        'Seed every lane overnight',
      ]),
      el('div', { class: 'cfh-checks' }, boxes),
      el('div', { class: 'cfh-liverow' }, [
        el('button', { class: 'cfh-btn', type: 'button', disabled: seedState.saving ? 'disabled' : null, text: seedState.saving ? 'Saving…' : 'Save', onclick: saveSeed }),
        seedState.msg ? line(seedState.msg, /fail|could not/i.test(seedState.msg) ? 'cfh-bad' : 'cfh-muted') : null,
      ]),
    ]);
  }

  function saveSeed() {
    var e = seedState.edit;
    seedState.saving = true; seedState.msg = '';
    if (lastData) render(lastData);
    api().post('/admin/freight-rate-cache-config', { seed: !!e.seed, seedDocks: e.docks.slice() }).then(function (res) {
      seedState.saving = false; seedState.edit = null;
      seedState.msg = 'Saved. ' + ((res && res.meaning) || '');
      load();
    }).catch(function (err) {
      seedState.saving = false;
      seedState.msg = 'Save failed: ' + ((err && err.message) || 'request failed') + '. Nothing was changed.';
      if (lastData) render(lastData);
    });
  }
  /* END CUSTOM_FREIGHTSEED_BY_CLAUDE */

  function parityBody(d) {
    var s = d.summary || {};
    var rows = (d.flagged || []).map(function (r) {
      return el('tr', null, [
        el('td', { text: r.ref || '-' }),
        el('td', { class: 'cfh-wrap', text: r.error ? ('Could not re-check: ' + r.error) : (r.sentence || 'flagged') }),
        el('td', { text: ageText(r.at) }),
      ]);
    });
    return el('div', null, [
      stats([
        stat('checks', n0(s.checks)),
        stat('flagged', n0(d.flaggedCount), d.flaggedCount ? 'cfh-stat--warn' : ''),
        stat('typical gap', s.meanAbsDiff == null ? '-' : money(s.meanAbsDiff)),
        stat('waiting', d.waiting == null ? '-' : n0(d.waiting)),
      ]),
      rows.length
        ? el('table', { class: 'cfh-table' }, [el('thead', null, [el('tr', null, [el('th', { text: 'Deal' }), el('th', { text: 'What was off' }), el('th', { text: 'When' })])]), el('tbody', null, rows)])
        : line(s.checks ? 'Nothing flagged.' : 'No website rates have been re-checked yet.', 'cfh-muted'),
    ]);
  }

  function accuracyBody(d) {
    var rows = (d.worst || []).map(function (p) {
      return el('tr', null, [
        el('td', { class: 'cfh-wrap', text: p.title || p.key || '-' }),
        el('td', { text: p.vendor || '-' }),
        el('td', { text: n0(p.loads) }),
        el('td', { class: p.hint === 'trust' ? 'cfh-good' : '', text: missWords(p) }),
      ]);
    });
    return el('div', null, [
      stats([
        stat('products ranked', n0(d.ranked)),
        stat('close enough to trust', n0(d.trusted)),
        stat('fewer than ' + d.minLoads + ' loads', n0(d.fewerLoads)),
        stat('shipments measured', n0(d.logs)),
      ]),
      rows.length
        ? el('table', { class: 'cfh-table' }, [el('thead', null, [el('tr', null, [el('th', { text: 'Product (worst first)' }), el('th', { text: 'Supplier' }), el('th', { text: 'Loads' }), el('th', { text: 'Typical miss' })])]), el('tbody', null, rows)])
        : line('No product has ' + d.minLoads + ' or more measured loads yet.', 'cfh-muted'),
    ]);
  }

  function liveCheckRow(v) {
    var st = live[v.vendor] || {};
    var result = st.loading ? line('Checking Shopify…', 'cfh-muted')
      : st.error ? line('Live check failed: ' + st.error, 'cfh-bad')
        : st.data ? line('Live in Shopify now: ' + n0(st.data.productsWithGaps) + ' products, ' + n0(st.data.unweighedVariants) + ' variants with no weight (' + n0(st.data.productsChecked) + ' checked).')
          : null;
    return el('div', { class: 'cfh-liverow' }, [
      el('button', { class: 'cfh-btn cfh-btn--ghost', type: 'button', disabled: st.loading ? 'disabled' : null, text: 'Check ' + v.vendor + ' live (' + v.count + ')', onclick: function () { checkLive(v.vendor); } }),
      result,
    ]);
  }

  function gapsBody(d) {
    var rows = (d.kinds || []).map(function (k) {
      return el('tr', null, [el('td', { class: 'cfh-wrap', text: k.label || k.id }), el('td', { class: k.count ? 'cfh-warn' : '', text: n0(k.count) })]);
    });
    var vendors = liveCheckVendors(d, 6);
    return el('div', null, [
      line(n0(d.activeProducts) + ' active products · list built ' + ageText(d.indexBuiltAt), 'cfh-muted'),
      el('table', { class: 'cfh-table' }, [el('thead', null, [el('tr', null, [el('th', { text: 'Gap' }), el('th', { text: 'Products' })])]), el('tbody', null, rows)]),
      vendors.length ? el('div', { class: 'cfh-live' }, vendors.map(liveCheckRow)) : null,
    ]);
  }

  function rocketBody(d) {
    var sp = d.speed || {}; var lim = d.limiter || {};
    return el('div', null, [
      stats([
        stat('credentials', d.configured ? 'set' : 'MISSING', d.configured ? '' : 'cfh-stat--bad'),
        stat('calls today' + (d.callsTodayIsFloor ? ' (at least)' : ''), n0(d.callsToday)),
        stat('this minute', n0(lim.thisMinute) + ' / ' + n0(lim.perMinute)),
        stat('timeouts', n0(sp.timeouts), sp.timeouts ? 'cfh-stat--warn' : ''),
      ]),
      stats([
        stat('median', secs(sp.medianMs)),
        stat('p90', secs(sp.p90Ms)),
        stat('slowest', secs(sp.maxMs)),
        stat('rating calls logged', n0(sp.count)),
      ]),
      d.missing && d.missing.length ? line('Not set: ' + d.missing.join(', '), 'cfh-bad') : null,
      line('Calls today are counted from the last ' + n0(d.logCap) + ' calls' + (d.oldestAt ? ' (oldest ' + ageText(d.oldestAt) + ')' : '') + '. Token ' + (d.tokenCached ? 'cached' : 'not cached') + '.', 'cfh-muted'),
    ]);
  }

  /* CUSTOM_FREIGHTCOMPARE_BY_CLAUDE (Freight Hub H4) */
  function compareKindBlock(k, label) {
    var r = k.readiness || {};
    var shareWithin = k.answered ? Math.round(((r.within || 0) / k.answered) * 100) + '%' : '-';
    return stats([
      stat(label, n0(k.n)),
      stat('stored price found', k.coveragePct == null ? '-' : pct(k.coveragePct), k.n && k.coveragePct < 50 ? 'cfh-stat--warn' : ''),
      stat('typical gap', gapWords(k.medianGapPct)),
      stat('within 10%', shareWithin),
    ]);
  }

  function compareTable(heads, rows) {
    return el('table', { class: 'cfh-table' }, [
      el('thead', null, [el('tr', null, heads.map(function (h) { return el('th', { text: h }); }))]),
      el('tbody', null, rows),
    ]);
  }

  function compareBody(d) {
    var q = d.quotes || {}; var b = d.billed || {}; var rule = d.rule || {};
    var rq = q.readiness || {};
    var dockRows = (q.byDock || []).map(function (x) {
      return el('tr', null, [
        el('td', { class: 'cfh-wrap', text: x.label || x.origin }),
        el('td', { text: n0(x.n) }),
        el('td', { text: n0(x.answered) }),
        el('td', { text: gapWords(x.medianGapPct) }),
        el('td', { text: x.answered ? n0(x.within) + ' of ' + n0(x.answered) : '-' }),
      ]);
    });
    var weekRows = (q.weekly || []).slice(-8).map(function (w) {
      return el('tr', null, [el('td', { text: w.week }), el('td', { text: n0(w.n) }), el('td', { text: gapWords(w.medianGapPct) }), el('td', { text: n0(w.within) + ' of ' + n0(w.n) })]);
    });
    var worst = (q.worst || []).concat(b.worst || []).sort(function (x, y) { return Math.abs(y.gapPct) - Math.abs(x.gapPct); }).slice(0, 8);
    var worstRows = worst.map(function (r) {
      return el('tr', null, [
        el('td', { text: r.ref || '-' }),
        el('td', { text: r.origin + ' > ' + r.destZip }),
        el('td', { text: n0(r.weight) + ' lb, class ' + r.cls }),
        el('td', { text: money(r.real) + (r.kind === 'billed' ? ' (billed)' : '') }),
        el('td', { text: money(r.stored && r.stored.cost) }),
        el('td', { class: r.gapPct < -5 ? 'cfh-bad' : '', text: gapWords(r.gapPct) }),
        el('td', { class: 'cfh-wrap', text: storedWhy(r) }),
      ]);
    });
    var bf = d.backfill;
    return el('div', null, [
      line(rq.sentence || 'No real quotes yet.', d.ready ? 'cfh-good' : ''),
      compareKindBlock(q, 'real quotes'),
      dockRows.length ? compareTable(['Dock', 'Quotes', 'Stored found', 'Typical gap', 'Within 10%'], dockRows) : null,
      weekRows.length ? el('p', { class: 'cfh-seed__title', text: 'By week (quotes)' }) : null,
      weekRows.length ? compareTable(['Week of', 'Quotes', 'Typical gap', 'Within 10%'], weekRows) : null,
      el('p', { class: 'cfh-seed__title', text: 'Final bills' }),
      line((b.readiness && b.readiness.sentence) || 'No billed loads yet.'),
      b.n ? compareKindBlock(b, 'billed loads') : null,
      worstRows.length ? el('p', { class: 'cfh-seed__title', text: 'Biggest misses' }) : null,
      worstRows.length ? compareTable(['Deal', 'Lane', 'Load', 'Real', 'Stored', 'Gap', 'Why'], worstRows) : null,
      line('Ready means: at least ' + n0(rule.minRates) + ' real quotes, within ' + rule.withinPct + '% nine times in ten, and under by more than ' + rule.underPct + '% no more than 1 time in 20. Nothing changes when it says ready.', 'cfh-muted'),
      bf ? line(bf.done ? 'First fill done: ' + n0(bf.scanned) + ' deals read, ' + n0(bf.rows) + ' rows.' : 'First fill running: ' + n0(bf.scanned) + ' deals read so far, ' + n0(bf.rows) + ' rows.', 'cfh-muted')
        : line('First fill has not started yet (it runs every 15 minutes).', 'cfh-muted'),
    ]);
  }

  var BODIES = { priceTable: priceTableBody, parity: parityBody, accuracy: accuracyBody, gaps: gapsBody, rocket: rocketBody, compare: compareBody };

  // ── load + render ──────────────────────────────────────────────────────────────────────────

  var lastData = null;

  function render(data) {
    clear(root);
    var bar = el('div', { class: 'cfh-bar' }, [
      line('Loaded ' + ageText(data.at) + (data.at ? ' (' + new Date(data.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) + ')' : ''), 'cfh-muted'),
      el('button', { class: 'cfh-btn', type: 'button', text: 'Refresh', onclick: load }),
    ]);
    var grid = el('div', { class: 'cfh-grid' }, SECTION_ORDER.map(function (name) {
      var s = data.sections[name];
      var body = null;
      if (s && s.ok) {
        try { body = BODIES[name](s.data || {}); } catch (e) { s = { ok: false, error: 'the page could not draw this section (' + (e && e.message) + ')' }; }
      }
      return tile(name, s, body);
    }));
    root.appendChild(bar);
    root.appendChild(grid);
  }

  function load() {
    clear(root);
    root.appendChild(notice('loading', 'Loading the Freight Hub…'));
    api().get('/admin/freight-hub/summary').then(function (data) {
      if (!summaryOk(data)) {
        clear(root);
        root.appendChild(notice('error', 'This page needs an update', 'The freight numbers arrived in a format this page does not understand. Reload the page; if it persists, tell Ryan.'));
        return;
      }
      lastData = data;
      render(data);
      /* H3: the calculator (custom-freight-hub-calc.js) takes its docks, default extras and markup from here. */
      window.FreightHub.lastSummary = data;
      try { window.dispatchEvent(new CustomEvent('freight-hub:summary', { detail: data })); } catch (e) { /* the calculator still works on a typed ZIP */ }
    }).catch(function (err) {
      clear(root);
      if (err && err.status === 403) {
        root.appendChild(notice('error', 'Admins only', 'The Freight Hub carries costs and broker rates, so only admins can open it.'));
        return;
      }
      var retry = el('button', { class: 'cfh-btn', type: 'button', text: 'Try again', onclick: load });
      root.appendChild(notice('error', 'The Freight Hub could not load', (err && err.message ? err.message + '. ' : '') + 'This is a loading problem, not an empty result.', retry));
    });
  }

  function checkLive(vendor) {
    live[vendor] = { loading: true };
    if (lastData) render(lastData);
    api().get('/freight-weight-gaps?vendor=' + encodeURIComponent(vendor)).then(function (data) {
      live[vendor] = { data: data };
    }).catch(function (err) {
      live[vendor] = { error: (err && err.message) || 'request failed' };
    }).then(function () { if (lastData) render(lastData); });
  }

  /* H2 (Ryan 2026-10-15): the freight settings panels under the tiles. They are shown only once an admin
     session is seen (the worker refuses their routes to anybody else), and each header opens and closes its
     panel - a copy of the Settings page's collapse handler, so nothing here reaches into Settings. */
  var settingsWired = false;
  function revealSettings() {
    var box = document.getElementById('freight-hub-settings');
    if (!box) return;
    box.hidden = false;
    if (settingsWired) return;
    settingsWired = true;
    var headers = box.querySelectorAll('[data-collapse-toggle]');
    for (var c = 0; c < headers.length; c++) {
      (function (header) {
        var section = header.parentElement;
        var body = section.querySelector('.rep-settings__section-body');
        var icon = header.querySelector('.rep-settings__collapse-icon');
        if (!body) return;
        header.addEventListener('click', function () {
          if (section.classList.contains('rep-settings__section--open')) {
            body.style.maxHeight = body.scrollHeight + 'px';
            body.offsetHeight; // reflow, so the close animates from the current height
            body.style.maxHeight = '0';
            section.classList.remove('rep-settings__section--open');
            if (icon) icon.textContent = '+';
          } else {
            body.style.maxHeight = body.scrollHeight + 'px';
            section.classList.add('rep-settings__section--open');
            if (icon) icon.textContent = '\u2212';
            var onEnd = function () {
              body.removeEventListener('transitionend', onEnd);
              if (section.classList.contains('rep-settings__section--open')) body.style.maxHeight = 'none';
            };
            body.addEventListener('transitionend', onEnd);
          }
        });
      })(headers[c]);
    }
  }

  // ── Boot ────────────────────────────────────────────────────────────────────────────────────

  var booted = false;
  function boot() {
    if (booted) return;
    root = document.getElementById('freight-hub-root');
    if (!root || !api()) return;
    var sess = null;
    try { sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || 'null'); } catch (e) { sess = null; }
    if (!sess) return; // still on the sign-in screen; `rep-portal:authenticated` retries
    booted = true;
    if (!sess.admin) {
      clear(root);
      root.appendChild(notice('error', 'Admins only', 'The Freight Hub carries costs and broker rates, so only admins can open it.'));
      return;
    }
    revealSettings();
    load();
  }

  window.FreightHub = { _pure: { extraLabel: extraLabel, feeWords: feeWords, missWords: missWords, liveCheckVendors: liveCheckVendors, summaryOk: summaryOk, ageText: ageText, secs: secs, pct: pct, gapWords: gapWords, storedWhy: storedWhy }, _boot: boot, _revealSettings: revealSettings };

  if (typeof document !== 'undefined' && document.addEventListener) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
    window.addEventListener('rep-portal:authenticated', boot);
  }
})();
/* END CUSTOM_FREIGHTHUB_BY_CLAUDE */
